import type { LucideIcon } from "lucide-react";
import { BUILTIN_ACTIVITY_TYPE_KEYS } from "@/lib/activitySettingsSchema";

/** Subconjunto usado para merge (evita importar o hook aqui). */
export type OrgActivityTypeRowLike = {
  id: string;
  key: string;
  label: string;
  icon: string | null;
  enabled: boolean;
  sort_order: number;
  is_system: boolean;
};

/** Defaults alinhados ao seed SQL (`organization_activity_types`). */
export const BUILTIN_ACTIVITY_TYPE_DEFAULTS: Record<
  (typeof BUILTIN_ACTIVITY_TYPE_KEYS)[number],
  { label: string; iconName: string }
> = {
  email: { label: "E-mail", iconName: "Mail" },
  call: { label: "Ligação", iconName: "Phone" },
  meeting: { label: "Reunião", iconName: "Video" },
  task: { label: "Tarefa", iconName: "CheckSquare" },
  note: { label: "Nota", iconName: "FileText" },
  proposal: { label: "Proposta", iconName: "FileText" },
  whatsapp: { label: "WhatsApp", iconName: "MessageCircle" },
};

const builtinKeySet = new Set<string>(BUILTIN_ACTIVITY_TYPE_KEYS);

function mergedBuiltinRows(
  orgRows: OrgActivityTypeRowLike[] | undefined
): Array<Pick<OrgActivityTypeRowLike, "key" | "label" | "icon" | "enabled" | "sort_order">> {
  const rows = orgRows || [];
  const byKey = new Map(rows.map((r) => [r.key, r]));
  return BUILTIN_ACTIVITY_TYPE_KEYS.map((key, index) => {
    const r = byKey.get(key);
    const def = BUILTIN_ACTIVITY_TYPE_DEFAULTS[key];
    if (r) {
      return {
        key: r.key,
        label: r.label,
        icon: r.icon,
        enabled: r.enabled,
        sort_order: r.sort_order,
      };
    }
    return {
      key,
      label: def.label,
      icon: def.iconName,
      enabled: true,
      sort_order: (index + 1) * 10,
    };
  });
}

/** Opções para modais de criar/editar: todos os tipos habilitados, com os 7 padrão sempre que possível (mesmo com linhas faltando no DB). */
export function buildMergedActivityPickerOptions(
  orgRows: OrgActivityTypeRowLike[] | undefined,
  resolveLucideIcon: (name?: string | null) => LucideIcon
): Array<{ value: string; label: string; icon: LucideIcon }> {
  const builtins = mergedBuiltinRows(orgRows);
  const out: Array<{ value: string; label: string; icon: LucideIcon }> = [];
  for (const r of builtins) {
    if (!r.enabled) continue;
    const def = BUILTIN_ACTIVITY_TYPE_DEFAULTS[r.key as keyof typeof BUILTIN_ACTIVITY_TYPE_DEFAULTS];
    const iconName = r.icon || def?.iconName;
    out.push({
      value: r.key,
      label: r.label,
      icon: resolveLucideIcon(iconName),
    });
  }
  const customs = (orgRows || [])
    .filter((r) => !builtinKeySet.has(r.key))
    .filter((r) => r.enabled)
    .sort((a, b) => a.sort_order - b.sort_order);
  for (const r of customs) {
    out.push({
      value: r.key,
      label: r.label,
      icon: resolveLucideIcon(r.icon),
    });
  }
  return out;
}

/** Lista completa (habilitados ou não) para selects de configuração — sempre inclui os 7 padrões com rótulo da org ou default. */
export function buildAllActivityTypeKeyOptions(
  orgRows: OrgActivityTypeRowLike[] | undefined
): Array<{ key: string; label: string }> {
  const builtins = mergedBuiltinRows(orgRows);
  const first: Array<{ key: string; label: string }> = builtins.map((r) => ({ key: r.key, label: r.label }));
  const seen = new Set(first.map((x) => x.key));
  const customs = (orgRows || [])
    .filter((r) => !seen.has(r.key))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((r) => ({ key: r.key, label: r.label }));
  return [...first, ...customs];
}

/** Ordem estável para a tela de admin: builtins na ordem canônica, depois customs por sort_order. */
export function sortActivityTypeRowsForAdmin<T extends OrgActivityTypeRowLike>(rows: T[]): T[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const builtins: T[] = [];
  for (const key of BUILTIN_ACTIVITY_TYPE_KEYS) {
    const r = byKey.get(key);
    if (r) builtins.push(r);
  }
  const customs = rows
    .filter((r) => !builtinKeySet.has(r.key))
    .sort((a, b) => a.sort_order - b.sort_order);
  return [...builtins, ...customs];
}
